import { useParams, useNavigate } from "react-router-dom";
import { useMemo } from "react";
import { Loader2 } from "lucide-react";
import { format } from "date-fns";

import MainLayout from "@/pages/layouts/MainLayout";
import { useBlogPost } from "@/shared/hooks/useBlogPosts";
import { Button } from "@/shared/components/ui/button";
import { POST_LOSS_ROUTES } from "@/shared/constants/paths";
import RelatedArticlesWidget from "../components/RelatedArticlesWidget";
import BlogCTA from "../components/BlogCTA";
import { ResourcesActions } from "../components/ResourcesActions";
import "./ArticlePage.css";

function ArticleBreadcrumb({ title }: { title: string }) {
  const navigate = useNavigate();
  return (
    <div className="flex items-center gap-2 text-sm">
      <button
        type="button"
        onClick={() => navigate(POST_LOSS_ROUTES.RESOURCES)}
        className="p-0 text-sm text-secondary hover:underline"
      >
        Community & Support
      </button>
      <span className="text-sm text-primary">/</span>
      <span className="text-sm text-primary">{title}</span>
    </div>
  );
}

export default function BlogArticle() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { data: post, isLoading, error } = useBlogPost(slug);

  // Process the content to split it into sections based on dynamic content placeholders
  const contentSections = useMemo(() => {
    if (!post?.fieldData["post-content"]) return [];

    let content = post.fieldData["post-content"];

    // Remove placeholders that should be deleted
    content = content
      .replace(new RegExp("{{blog-cta}}", "g"), "")
      .replace(new RegExp("{{blog-cta-small}}", "g"), "")
      .replace(new RegExp("{{blog-cta-admin}}", "g"), "")
      .replace(new RegExp("{{blog-cta-hook1-large}}", "g"), "")
      .replace(new RegExp("{{blog-cta-hook1-small}}", "g"), "")
      .replace(new RegExp("{{blog-cta-hook2-large}}", "g"), "")
      .replace(new RegExp("{{blog-cta-hook2-small}}", "g"), "");

    // Replace internal blog links with app routes
    // Match links like https://www.elayne.com/resources/{slug} or http://www.elayne.com/resources/{slug}
    content = content.replace(
      /https?:\/\/www\.elayne\.com\/resources\/([^"'\s<>]+)/g,
      (_match, slug) => {
        // Extract just the slug (remove any trailing path segments or query params)
        const cleanSlug = slug.split(/[?#]/)[0];
        return POST_LOSS_ROUTES.RESOURCES_ARTICLE.replace(":slug", cleanSlug);
      },
    );

    // Add target="_blank" and rel="noopener noreferrer" to all external links
    // Match <a> tags that have href attributes
    content = content.replace(/<a\s+([^>]*href=["']([^"']*)["'][^>]*)>/gi, (match, attrs, href) => {
      // Skip if it's an internal app route (starts with /post-loss/resources/)
      if (href.startsWith("/post-loss/community-and-resources/")) {
        return match;
      }

      // Check if target and rel are already present
      const hasTarget = /target=/i.test(attrs);
      const hasRel = /rel=/i.test(attrs);

      // Build the new attributes
      let newAttrs = attrs;
      if (!hasTarget) {
        newAttrs += ' target="_blank"';
      }
      if (!hasRel) {
        newAttrs += ' rel="noopener noreferrer"';
      }

      return `<a ${newAttrs}>`;
    });

    // Replace CTA checklist placeholders with unique markers
    const smallPlaceholder = "{{blog-cta-checklist-small}}";
    const largePlaceholder = "{{blog-cta-checklist-large}}";
    const smallMarker = "___CTA_SMALL___";
    const largeMarker = "___CTA_LARGE___";

    content = content
      .replace(new RegExp(smallPlaceholder, "g"), smallMarker)
      .replace(new RegExp(largePlaceholder, "g"), largeMarker);

    // Split by both markers
    const parts = content.split(/(___CTA_SMALL___|___CTA_LARGE___)/);

    // Create sections
    const sections: Array<{
      type: "html" | "cta";
      content: string;
    }> = [];

    parts.forEach((part) => {
      if (part === smallMarker || part === largeMarker) {
        sections.push({ type: "cta", content: "" });
      } else if (part.trim()) {
        sections.push({ type: "html", content: part });
      }
    });

    return sections;
  }, [post]);

  if (isLoading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-6 h-6 animate-spin text-stone-400" />
        </div>
      </MainLayout>
    );
  }

  if (error || !post) {
    return (
      <MainLayout>
        <div className="flex flex-col items-center justify-center min-h-[400px]">
          <p className="text-stone-600 mb-4">Article not found</p>
          <Button variant="outline" onClick={() => navigate(POST_LOSS_ROUTES.RESOURCES)}>
            Back
          </Button>
        </div>
      </MainLayout>
    );
  }

  const articleTitle = post.fieldData["titletag"] || post.fieldData.name;

  return (
    <MainLayout header={<ArticleBreadcrumb title={articleTitle} />} actions={<ResourcesActions />}>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-10 mt-[24px]">
        {/* Left column - 8 columns (main content area) */}
        <div className="flex flex-col col-span-1 lg:col-span-8">
          <article>
            {/* Title */}
            <h1 className="font-[400] line-height-[36px] text-[32px] text-primary tracking-[-0.225px] mt-[32px]">
              {articleTitle}
            </h1>

            {/* Meta Description */}
            {post.fieldData["meta-description"] && (
              <p className="mt-[32px] text-base font-normal line-height-[28px] text-primary">
                {post.fieldData["meta-description"]}
              </p>
            )}

            {/* Hero Image */}
            {post.fieldData["main-image"]?.url && (
              <div className="rounded-xl overflow-hidden mt-[32px]">
                <img
                  src={post.fieldData["main-image"].url}
                  alt={post.fieldData.name}
                  className="w-full h-auto"
                />
              </div>
            )}

            {/* Date */}
            <p className="mt-[8px] text-sm font-normal line-height-[20px] text-stone-500">
              {format(new Date(post.lastPublished), "MMMM d, yyyy")}
            </p>

            {/* Post Content */}
            {post.fieldData["post-content"] && (
              <div className="mt-[32px]">
                {contentSections.map((section, index) => {
                  if (section.type === "cta") {
                    return (
                      <BlogCTA
                        key={`cta-${index}`}
                        subhead={post.fieldData["small-cta-subhead"]}
                        urlParameter={post.fieldData["new-cta-url-parameter"]}
                      />
                    );
                  }
                  return (
                    <div
                      key={`content-${index}`}
                      className="blog-article-content"
                      dangerouslySetInnerHTML={{ __html: section.content }}
                    />
                  );
                })}
              </div>
            )}
          </article>
        </div>

        {/* Right column - 4 columns (sidebar area) */}
        <div className="flex flex-col gap-8 col-span-1 lg:col-span-4 mt-[64px]">
          <RelatedArticlesWidget currentSlug={slug || ""} />
        </div>
      </div>
    </MainLayout>
  );
}
